import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Skeleton,
} from "@pushpress/pushpress-ui";
import {
  Cake,
  Phone,
  Mail,
  Users,
  CalendarCheck,
  Calendar,
} from "lucide-react";
import { cn } from "../../../utils/css";
import { format, parseISO } from "date-fns";
import { useSectionContext } from "../hooks/useSectionContext";
import { LegacyStyleWrapper } from "./shared/LegacyStyleWrapper";
import {
  convertDateStringOrTimestampToUsDateFormat,
  getParentAccountInfo,
  getUserType,
} from "../helpers";
import { EMPTY_VALUE } from "../constants";
import type { V2Member } from "../../../apis/userApi";
import type { UsersInfoType } from "../types";

type ProfileHeaderProps = {
  profileData?: V2Member;
  isDataLoading: boolean;
  usersInfo?: UsersInfoType;
  isUsersInfoFetching: boolean;
};

export default function ProfileHeader({
  profileData,
  isDataLoading,
  usersInfo,
  isUsersInfoFetching,
}: ProfileHeaderProps) {
  const { scrollToSection, activeSection } = useSectionContext();

  const showUserDetails = activeSection === "summary";

  const hasProfileData = profileData && !isDataLoading;
  const hasUsersInfo = usersInfo && !isUsersInfoFetching;

  const isBirthday =
    profileData && profileData.dob
      ? format(new Date(), "MM-dd") ===
        format(parseISO(profileData.dob), "MM-dd")
      : false;

  /* Filtering on clientUuid is needed because linkedAccounts
  can contain sub-accounts for other clients */
  const subAccounts =
    (profileData &&
      profileData?.linkedAccounts?.filter(
        (subAccount) => subAccount.clientUuid === profileData.clientUuid,
      )) ||
    [];

  const hasSubAccounts = subAccounts && subAccounts.length > 0;

  const isSubAccount = profileData && profileData.parentUserId !== 0;
  const parentAccountInfo = getParentAccountInfo(profileData, usersInfo);
  const parentAccountName = parentAccountInfo
    ? `${parentAccountInfo?.firstName} ${parentAccountInfo?.lastName}`
    : EMPTY_VALUE;

  const transitionDuration = "tw-transition-all tw-duration-200 tw-ease-in-out";

  return (
    <LegacyStyleWrapper>
      <div
        className={cn(
          "tw-border-b tw-px-4 tw-bg-white",
          transitionDuration,
          showUserDetails ? "tw-py-4" : "tw-py-2.5",
        )}
      >
        <div className="tw-flex tw-items-start tw-justify-between">
          <div
            className={cn(
              "tw-flex tw-items-center",
              transitionDuration,
              showUserDetails ? "tw-gap-10" : "tw-gap-4",
            )}
          >
            <div className="tw-relative">
              {hasProfileData ? (
                <Avatar
                  className={cn(
                    transitionDuration,
                    showUserDetails
                      ? "tw-h-[88px] tw-w-[88px]"
                      : "tw-h-12 tw-w-12",
                  )}
                >
                  <AvatarImage
                    src={profileData.primaryImage}
                    alt={profileData.firstName}
                  />
                  <AvatarFallback className="tw-text-xl tw-text-primary">
                    {profileData.firstName?.charAt(0).toUpperCase()}
                    {profileData.lastName?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <Skeleton
                  className={
                    showUserDetails ? "tw-h-24 tw-w-24" : "tw-h-12 tw-w-12"
                  }
                />
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-justify-center">
              <div className="tw-flex tw-gap-2 tw-items-center">
                {hasProfileData ? (
                  <h1
                    className={cn(
                      "tw-font-bold",
                      transitionDuration,
                      showUserDetails ? "tw-text-[32px]" : "tw-text-2xl",
                    )}
                  >
                    {profileData.firstName} {profileData.lastName}
                  </h1>
                ) : (
                  <Skeleton className="tw-h-4 tw-w-24" />
                )}
                {hasProfileData ? (
                  <Badge variant="outline" className="!tw-text-neutral-800">
                    {getUserType(profileData.membershipRole)}
                  </Badge>
                ) : (
                  <Skeleton className="tw-h-4 tw-w-24" />
                )}
                {isBirthday && (
                  <Badge variant="error">
                    <Cake className="tw-h-3 tw-w-3 tw-mr-1" />
                    Birthday
                  </Badge>
                )}
                {hasSubAccounts && hasUsersInfo && (
                  <Badge
                    onClick={() =>
                      scrollToSection("personal-info", "smooth", "end")
                    }
                    className="tw-cursor-pointer"
                  >
                    <Users className="tw-h-3 tw-w-3 tw-mr-1" />
                    Sub-accounts ({subAccounts.length})
                  </Badge>
                )}
                {isSubAccount && hasUsersInfo && (
                  <Badge
                    onClick={() =>
                      scrollToSection("personal-info", "smooth", "end")
                    }
                    className="tw-cursor-pointer"
                  >
                    <Users className="tw-h-3 tw-w-3 tw-mr-1" />
                    Sub-account of {parentAccountName}
                  </Badge>
                )}
              </div>
              <div
                className={cn(
                  "tw-overflow-hidden",
                  transitionDuration,
                  showUserDetails
                    ? "tw-max-h-20 tw-opacity-100 tw-mt-2"
                    : "tw-max-h-0 tw-opacity-0 tw-mt-0",
                )}
              >
                <div className="tw-flex tw-items-center tw-text-sm tw-gap-8">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-neutral-800">
                    <CalendarCheck className="tw-h-3.5 tw-w-3.5" />
                    {hasProfileData ? (
                      <span>
                        Last check-in:{" "}
                        {convertDateStringOrTimestampToUsDateFormat(
                          profileData.lastCheckedInAt,
                        )}
                      </span>
                    ) : (
                      <Skeleton className="tw-h-3.5 tw-w-24" />
                    )}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-neutral-600">
                    <Calendar className="tw-h-3.5 tw-w-3.5" />
                    {hasProfileData ? (
                      <span>
                        Member since:{" "}
                        {convertDateStringOrTimestampToUsDateFormat(
                          profileData.memberSince,
                        )}
                      </span>
                    ) : (
                      <Skeleton className="tw-h-3.5 tw-w-24" />
                    )}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-neutral-600">
                    <Phone className="tw-h-3.5 tw-w-3.5" />
                    {hasProfileData ? (
                      <span>{profileData.phone || EMPTY_VALUE}</span>
                    ) : (
                      <Skeleton className="tw-h-3.5 tw-w-24" />
                    )}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Mail className="tw-h-3.5 tw-w-3.5 tw-text-neutral-600" />
                    {hasProfileData ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="!tw-p-0 tw-h-auto tw-text-primary"
                      >
                        {profileData.email || EMPTY_VALUE}
                      </Button>
                    ) : (
                      <Skeleton className="tw-h-3.5 tw-w-24" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LegacyStyleWrapper>
  );
}
